import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible/Collapsible";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { Eye, EyeClosed, ListChecks } from "lucide-react";
import { useEffect, useState } from "react";
import { AttendanceTable } from "./AttendanceDataTable";
import { formatNumber } from "@/utils/formatNumber";

const Attendance = ({
  data,
  loading,
}: {
  data: {
    total: { records: number; present: number };
    rotations: any[];
  } | null;
  loading: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
  }, [data]);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      style={{ marginTop: "-8px" }}
    >
      <VStack gap="2rem">
        <VStack gap={0}>
          <HStack>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                lineHeight: "var(--line-height-lg)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Attendance
            </h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon-small">
                    {open ? <EyeClosed /> : <Eye />}
                  </Button>
                </CollapsibleTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} attendance section.
              </TooltipContent>
            </Tooltip>
          </HStack>
          <p style={{ color: "var(--muted-foreground)" }}>
            Your attendance stats
          </p>
        </VStack>
        <CollapsibleContent>
          {loading ? (
            <VStack gap="1rem">
              <HStack gap="1rem">
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
              </HStack>
              <Skeleton style={{ height: "240px", width: "100%" }} />
            </VStack>
          ) : !data || data.rotations.length < 1 ? (
            <Empty style={{ borderColor: "var(--secondary-border)" }}>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ListChecks />
                </EmptyMedia>
                <EmptyTitle>No Attendance Records Found</EmptyTitle>
                <EmptyDescription>
                  There were no attendance records found. If you believe this is
                  an error, please refresh.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <VStack gap="1rem">
              <HStack gap="1rem" style={{ flexWrap: "wrap" }}>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Total Records</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.total.records)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Present</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.total.present)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Absent</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.total.records - data.total.present)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Rate</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {((data.total.present / data.total.records) * 100).toFixed(
                      1,
                    )}
                    %
                  </p>
                </DashboardCard>
              </HStack>
              <AttendanceTable data={data.rotations} />
            </VStack>
          )}
        </CollapsibleContent>
      </VStack>
    </Collapsible>
  );
};

export { Attendance };
